/**
 * Controller for the Dynamic Template Provider widget (MD-148).
 *
 * Derives draggable template handles for the currently selected patient from
 * the Rx "lacking appointments" data (prescribed amount vs. already scheduled
 * count per therapy). Read-only for Phase 1 — no drag/create wiring yet.
 */

import { useCallback, useMemo } from "react";
import useStore from "../../../helpers/useStore";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import { UserContext } from "../../../stores/User/User.provider";
import { useRxLackingAppointments, useTemplateWidgetData } from "../../../stores/Rx/rx.queries";
import { useAppointmentsByPatient } from "../../../stores/Appointment/appointment.queries";
import {
  PASSIVE_ABBREVIATIONS_AT_ENTRY,
  slotTherapy,
  therapyNamingOrder,
} from "../../../helpers/slotTherapy";
import { useTermDefaultDurationLookup } from "../../../stores/MasterTables/masterTables.queries";
import type { SlotDefaultDurationLookup } from "../controller/adapters/client-to-draggable/appointment.adapter";
import { TemplateHandle, TemplateHandleTherapy } from "./types";
import { isPracticeRx } from "./templateAddress";
import { useTemplateAddresses } from "./useTemplateAddresses";
import { AddressDto, AppointmentDto, RxDto, TemplateWidgetDefaultSelectionDto } from "../../../api-client";

/** Permission gate — keep the in-progress widget behind the feature preview flag. */
const FEATURE_PREVIEW_PERMISSION = "featurePreview";

/**
 * Infer the address and per-therapy visit fee for a set of therapyRx ids from
 * the patient's existing appointments. For each therapyRx we copy the address
 * and visit-fee decision (a fee id, or `null` for an explicit "no fee") from
 * the most recent appointment that already scheduled that therapy. This covers
 * the "remembered config" stories: once one appointment of an Rx exists, the
 * next drag reuses its address/fee without a dialog.
 */
export interface ProvisionInference {
  addressId?: string;
  visitFees: Record<string, string | null>;
  needsInput: boolean;
  /** Display data for the primed handle: Haus/Heim/Praxis. */
  visitType?: string;
  /** Display data: first address line of the inferred address. */
  addressLabel?: string;
  /** Display data: fee abbreviation per therapyRxId; "keine" for explicit no-fee. */
  visitFeeLabels: Record<string, string>;
}

const inferFromExistingAppointments = (
  therapyRxIds: string[],
  appointments: AppointmentDto[]
): ProvisionInference => {
  // Most recent first so the freshest decision wins.
  const sorted = [...appointments].sort((a, b) => {
    const ta = a.start ? new Date(a.start).getTime() : 0;
    const tb = b.start ? new Date(b.start).getTime() : 0;
    return tb - ta;
  });

  const visitFees: Record<string, string | null> = {};
  const visitFeeLabels: Record<string, string> = {};
  let addressId: string | undefined;
  let visitType: string | undefined;
  let addressLabel: string | undefined;

  for (const therapyRxId of therapyRxIds) {
    const appt = sorted.find((a) =>
      a.timeSlots?.some((ts) => ts.therapyRx?.id === therapyRxId)
    );
    if (!appt) continue; // brand-new therapy — decision still open
    const slot = appt.timeSlots?.find((ts) => ts.therapyRx?.id === therapyRxId);
    // A missing visitFee on an existing slot is itself a deliberate "no fee".
    visitFees[therapyRxId] = slot?.visitFee?.id ?? null;
    visitFeeLabels[therapyRxId] = (slot?.visitFee as any)?.abbreviation ?? "keine";
    // First (most recent) resolved address wins.
    if (!addressId && appt.address?.id) {
      addressId = appt.address.id;
      visitType = (appt.address as any)?.type ?? undefined;
      addressLabel = (appt.address as any)?.contactInfo?.addressLine1 ?? undefined;
    }
  }

  const allDecided = therapyRxIds.every((id) => id in visitFees);
  const needsInput = !addressId || !allDecided;

  return { addressId, visitFees, needsInput, visitType, addressLabel, visitFeeLabels };
};

/**
 * Resolve the address / visit-fee decision for a template handle.
 *
 * An Rx that is not flagged for home visits is a PRACTICE appointment, and
 * there is no ambiguity to resolve: it takes the practice address with no visit
 * fee, so its drop skips the missing-data dialog entirely. The practice address
 * comes from the current location (it is not among the patient's addresses),
 * which is why `practiceAddress` is passed in rather than searched for here.
 *
 * When the location has no address on file the fast path cannot fire — we fall
 * back to the patient's prior appointments, and if those yield nothing the
 * handle stays bare so the dialog can ask. An address is mandatory; guessing
 * one, or priming without one, is not an option.
 *
 * Home-visit Rxs (`isHomeVisit === true`) always go through inference: the
 * practice-vs-home choice is a real decision, so it is either copied from a
 * previous appointment of the same Rx or asked for.
 */
const inferForRx = (
  rx: any,
  therapyRxIds: string[],
  appointments: AppointmentDto[],
  practiceAddress: AddressDto | undefined
): ProvisionInference => {
  const inference = ((): ProvisionInference => {
    if (isPracticeRx(rx?.isHomeVisit) && practiceAddress?.id) {
      const visitFees: Record<string, string | null> = {};
      const visitFeeLabels: Record<string, string> = {};
      for (const id of therapyRxIds) {
        visitFees[id] = null;
        visitFeeLabels[id] = "keine";
      }
      return {
        addressId: practiceAddress.id,
        visitFees,
        needsInput: false,
        visitType: "Praxis",
        addressLabel:
          (practiceAddress as any)?.contactInfo?.addressLine1 ?? undefined,
        visitFeeLabels,
      };
    }
    return inferFromExistingAppointments(therapyRxIds, appointments);
  })();

  // INVARIANT: a handle is "primed" only if it can actually be scheduled, and
  // no appointment may exist without an address. Enforced here, at the single
  // place every handle's provisions are decided, rather than trusting each
  // branch to have got `needsInput` right — a primed handle with a null address
  // is a drop that either creates an addressless appointment or dies at the
  // API, and the chip would have promised otherwise.
  return { ...inference, needsInput: inference.needsInput || !inference.addressId };
};

/**
 * Therapy fields for a template handle, read from the PRESCRIBED term.
 *
 * A template is by definition unscheduled, so its therapyRx has no line item
 * yet — `TherapyRxDto.therapy` was removed in the prescribed-therapy refactor
 * and only `prescribedTherapy` remains. The old code read `trx.therapy` on an
 * `any`, so the rename produced empty chip labels silently instead of a type
 * error.
 *
 * `duration` comes from the term × tarif group master-data default — the
 * duration of the line item `resolveTherapy` would pick for a fully-
 * credentialed therapist (see useTermDefaultDurationLookup). It is a planning
 * guess only: the server re-resolves the line item at create time and its
 * survivor DTO sets the real window. Without it every chip dragged 30 minutes
 * regardless of therapy — KG and MT are 20 — so a "KG + PA" bundle asked the
 * calendar for an hour it does not need. 30 stays as the last-resort fallback
 * for a term with no in-use line item anywhere (the same value
 * `adaptTherapyFromApi` falls back to).
 *
 * The lookup is per (term, tarif group) and answers nothing without the tarif
 * group — deliberately, since 32a2631d: durations differ by payer (PFB is 60
 * under GKV, 75 under DGUV) and borrowing "any" entry laid Ergo Blanko series
 * out at the wrong length. So the caller must actually supply it, and the Rx
 * arrives in two shapes: the widget feeds (`/rx/lackingAppointments`,
 * `/rx/templateWidgetData`) select the relation `tarifGroup: { id }` — the Rx
 * entity has no `tarifGroupId` column — while shelf payloads set the bare FK.
 * Reading only the FK returned undefined for every template handle, and every
 * chip drew 30 again (v0.4.0–v0.4.1). See rxTarifGroupIdOf and
 * docs/planning-handles.md, "How a handle gets its length".
 *
 * What a prescribed term still cannot provide, and what happens instead:
 * - `therapyId`: the tarif-bound therapy id does not exist yet. The calendar's
 *   credential gate is `if (therapy?.id)`, so template drops skip the
 *   client-side credential check and the server validates on create. (True
 *   since the refactor; recorded here rather than silently.)
 * - passive/heat: no `isPassive` flag on a term, so the entry-abbreviation
 *   stopgap decides — see PASSIVE_ABBREVIATIONS_AT_ENTRY.
 */
/**
 * The Rx's tarif group id in either shape an Rx reaches the client in: the
 * bare FK (`tarifGroupId`, set by hand on the shelf payloads) or the selected
 * relation (`tarifGroup.id`, what the widget feeds carry). Mirrors
 * termDefaultDurationOf in the draggable adapter, which reads both for the
 * same reason. Null when the Rx has neither.
 */
export const rxTarifGroupIdOf = (rx: any): string | null =>
  rx?.tarifGroupId ?? rx?.tarifGroup?.id ?? null;

const templateTherapyFromPrescribed = (
  trx: any,
  tarifGroupId?: string | null,
  defaultDuration?: SlotDefaultDurationLookup
) => {
  const term = slotTherapy({ therapyRx: trx });
  const abbreviation = term?.abbreviation ?? "";
  return {
    therapyId: "",
    abbreviation,
    duration:
      defaultDuration?.(
        trx?.prescribedTherapyId ?? trx?.prescribedTherapy?.id ?? null,
        tarifGroupId ?? null
      ) ?? 30,
    isHeatTreatment: PASSIVE_ABBREVIATIONS_AT_ENTRY.includes(abbreviation),
    isSupplementary: term?.isSupplementary === true,
  };
};

/** An Rx therapy as the precedent derivation needs it: identity + prescribed term. */
export interface PrecedentTherapy {
  therapyRxId: string;
  prescribedTherapyId: string;
}

/**
 * Precedent groupings: which of this Rx's therapy TYPES the patient's existing
 * appointments actually schedule together. Each group is a set of this Rx's
 * therapyRx ids whose prescribed terms co-occurred in one appointment (2+),
 * deduplicated, most recent first. This is the composition source for
 * SUGGESTION bundles (docs/planning-handles.md).
 *
 * Matching happens on the prescribed TERM id, not the therapyRx id: therapyRx
 * ids are per-prescription, so an id match would make every follow-up Rx start
 * with no precedent even though the patient has received e.g. MT + KG together
 * for months. The term is the identity that carries the habit across Rxs.
 */
export const derivePrecedentGroups = (
  rxTherapies: PrecedentTherapy[],
  appointments: AppointmentDto[] | undefined
): string[][] => {
  // Term -> this Rx's therapyRx. First one wins should an Rx ever prescribe
  // the same term twice — the caller passes them in prescription order.
  const trxByTerm = new Map<string, string>();
  for (const t of rxTherapies) {
    if (t.prescribedTherapyId && !trxByTerm.has(t.prescribedTherapyId)) {
      trxByTerm.set(t.prescribedTherapyId, t.therapyRxId);
    }
  }
  const byKey = new Map<string, { termIds: string[]; lastStart: string }>();
  for (const appt of appointments ?? []) {
    const termIds = Array.from(
      new Set(
        (appt.timeSlots ?? [])
          .map((ts: any) =>
            String(
              ts.therapyRx?.prescribedTherapy?.id ?? ts.therapyRx?.prescribedTherapyId ?? ""
            )
          )
          .filter((id) => id && trxByTerm.has(id))
      )
    ).sort();
    if (termIds.length < 2) continue;
    const key = termIds.join("+");
    const start = String((appt as any).start ?? "");
    const prev = byKey.get(key);
    if (!prev || start > prev.lastStart) byKey.set(key, { termIds, lastStart: start });
  }
  return Array.from(byKey.values())
    .sort((a, b) => (a.lastStart < b.lastStart ? 1 : -1))
    .map((g) => g.termIds.map((id) => trxByTerm.get(id) as string));
};

/** The precedent-derivation view of an Rx's therapyRxs, in prescription order. */
const precedentTherapiesOf = (therapyRxs: any[]): PrecedentTherapy[] =>
  [...therapyRxs]
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
    .map((trx) => ({
      therapyRxId: String(trx?.id ?? ""),
      prescribedTherapyId: String(trx?.prescribedTherapy?.id ?? trx?.prescribedTherapyId ?? ""),
    }));

/**
 * Build the list of template handles from the lacking-appointment Rxs.
 *
 * For each non-perpetual Rx:
 *  - every therapy with remaining (amount - scheduledCount) > 0 yields a single handle,
 *  - each precedent grouping (therapy types the patient's appointments already
 *    schedule together, across Rxs) yields a combined SUGGESTION handle,
 *  - without precedent, one narrow fallback: exactly one remaining therapy plus
 *    one supplementary application bundles as a combi (the KG + PA case).
 *
 * Each handle is enriched with address/visit-fee inferred from the patient's
 * existing appointments so a drop can skip the dialog when possible.
 *
 * `defaultDuration` is the master-data lookup that gives each chip a realistic
 * length (see templateTherapyFromPrescribed). Optional because this is a pure
 * function the tests call directly; omitting it falls back to 30 per therapy.
 */
export const deriveHandles = (
  rxList: any[] | undefined,
  appointments: AppointmentDto[],
  practiceAddress: AddressDto | undefined,
  defaultDuration?: SlotDefaultDurationLookup
): TemplateHandle[] => {
  if (!rxList?.length) return [];

  const handles: TemplateHandle[] = [];

  for (const rx of rxList) {
    if (rx?.isPerpetual) continue;
    const rxId = String(rx?.id ?? "");
    const rxNumber = String(rx?.rxNumber ?? "");
    const rxDate: string | undefined = rx?.date ?? undefined;
    const therapyRxs: any[] = Array.isArray(rx?.therapyRxs) ? rx.therapyRxs : [];

    const remainingTherapies: TemplateHandleTherapy[] = therapyRxs
      .map((trx) => {
        const remaining = (trx?.amount ?? 0) - (trx?.scheduledCount ?? 0);
        return {
          therapyRxId: String(trx?.id ?? ""),
          ...templateTherapyFromPrescribed(trx, rxTarifGroupIdOf(rx), defaultDuration),
          order: trx?.order ?? 0,
          remaining,
        } as TemplateHandleTherapy;
      })
      .filter((t) => t.remaining > 0)
      .sort((a, b) => a.order - b.order);

    if (remainingTherapies.length === 0) continue;

    // Single handles — one per remaining therapy.
    for (const therapy of remainingTherapies) {
      const inference = inferForRx(rx, [therapy.therapyRxId], appointments, practiceAddress);
      handles.push({
        id: `${rxId}:single:${therapy.therapyRxId}`,
        rxId,
        rxNumber,
        rxDate,
        kind: "single",
        therapies: [therapy],
        label: therapy.abbreviation,
        remaining: therapy.remaining,
        provenance: "amounts",
        inferredAddressId: inference.addressId,
        inferredVisitFees: inference.visitFees,
        inferredVisitType: inference.visitType,
        inferredAddressLabel: inference.addressLabel,
        inferredVisitFeeLabels: inference.visitFeeLabels,
        needsInput: inference.needsInput,
      });
    }

    // Bundles. A bundle composes several therapies into ONE appointment; the
    // interesting question is WHICH ones belong together, and there are two
    // answers with different standing:
    //  - "precedent": the patient's existing appointments already group these
    //    therapy types — a SUGGESTION mirroring the established habit.
    //  - "amounts": without precedent, the only pairing safe to assume is a
    //    lone therapy with its lone supplementary application (KG + PA) —
    //    supplements exist to be given alongside a main therapy.
    // Deduplicated by therapy-id set, so when the habit IS that pairing only
    // one bundle renders.
    const remainingById = new Map(remainingTherapies.map((t) => [t.therapyRxId, t]));
    const emittedSets = new Set<string>();
    const pushBundle = (
      bundleTherapies: TemplateHandleTherapy[],
      provenance: "precedent" | "amounts"
    ) => {
      const therapyRxIds = bundleTherapies.map((t) => t.therapyRxId);
      const setKey = [...therapyRxIds].sort().join("+");
      if (emittedSets.has(setKey)) return;
      emittedSets.add(setKey);
      const inference = inferForRx(rx, therapyRxIds, appointments, practiceAddress);
      handles.push({
        id: `${rxId}:combined:${therapyRxIds.join("-")}`,
        rxId,
        rxNumber,
        rxDate,
        kind: "combined",
        therapies: bundleTherapies,
        label: therapyNamingOrder(bundleTherapies, (t) => t.isSupplementary, (t) => t.abbreviation)
          .map((t) => t.abbreviation)
          .join(" + "),
        remaining: Math.min(...bundleTherapies.map((t) => t.remaining)),
        provenance,
        inferredAddressId: inference.addressId,
        inferredVisitFees: inference.visitFees,
        inferredVisitType: inference.visitType,
        inferredAddressLabel: inference.addressLabel,
        inferredVisitFeeLabels: inference.visitFeeLabels,
        needsInput: inference.needsInput,
      });
    };

    for (const group of derivePrecedentGroups(precedentTherapiesOf(therapyRxs), appointments)) {
      // Only suggest what is still schedulable: every member must have
      // remaining amount.
      const members = group.map((id) => remainingById.get(id));
      if (members.some((m) => !m)) continue;
      pushBundle(
        (members as TemplateHandleTherapy[]).slice().sort((a, b) => a.order - b.order),
        "precedent"
      );
    }

    if (
      remainingTherapies.length === 2 &&
      remainingTherapies.filter((t) => t.isSupplementary).length === 1
    ) {
      pushBundle(remainingTherapies, "amounts");
    }
  }

  return handles;
};

export const useDynamicTemplateProviderController = () => {
  const CalendarStore = useStore(CalendarContext);
  const UserStore = useStore(UserContext);

  const { selectedPatientId } = CalendarStore;
  // Vorlagen are off in the continuous (Dauertermin) view: a drop goes to
  // createAndSchedule, which only creates regular, dated appointments — there
  // is no continuous variant. Offered there, a chip "scheduled" into the normal
  // calendar and vanished from the DT one. Every consumer (band section, header
  // expander, hotkeys, data feed) gates on this flag, so this hides all of it.
  const canSeeFeaturePreview =
    UserStore.hasPermission(FEATURE_PREVIEW_PERMISSION) && !CalendarStore.continuousView;

  const { data: rxLackingAppointments = [], isLoading } = useRxLackingAppointments(
    selectedPatientId!
  );

  // Existing appointments power address/visit-fee inference so a drop can skip
  // the missing-data dialog when the Rx has already been scheduled before.
  const { data: existingAppointments = [] } = useAppointmentsByPatient(selectedPatientId!);

  // The practice address, from the CURRENT LOCATION — it is not one of the
  // patient's addresses. Non-house-visit Rxs always schedule there with no
  // visit fee and no dialog. Shared with TemplateScheduleDialog through
  // useTemplateAddresses so the primed handle and the dialog agree on it.
  const { practiceAddress } = useTemplateAddresses(selectedPatientId ?? undefined);

  // Provisional chip lengths from the 12h-cached catalog feed — the same
  // lookup the shelf's drag adapters use, so a term-only drop is laid out the
  // same length whether it comes from an unscheduled appointment or a handle.
  const termDefaultDuration = useTermDefaultDurationLookup();

  // Manual-selection mode: the widget can be summoned by the user even when no
  // appointments are lacking. We only fetch its (broader) data feed once it's
  // actually open to avoid an extra request on every patient.
  const isOpen = CalendarStore.templateWidgetOpen;
  const { data: widgetData, isLoading: isWidgetDataLoading } = useTemplateWidgetData(
    selectedPatientId!,
    canSeeFeaturePreview && !!selectedPatientId && isOpen
  );
  const selectableRxs: RxDto[] = widgetData?.selectableRxs ?? [];
  const defaultSelection: TemplateWidgetDefaultSelectionDto | null =
    widgetData?.defaultSelection ?? null;

  const handles = useMemo(
    () =>
      deriveHandles(
        rxLackingAppointments,
        existingAppointments,
        practiceAddress,
        termDefaultDuration
      ),
    [rxLackingAppointments, existingAppointments, practiceAddress, termDefaultDuration]
  );

  /**
   * Build an on-the-fly single-therapy handle from an arbitrary Rx + therapyRx
   * the user picked in the manual selector. Reuses the same address/visit-fee
   * inference as the auto-derived handles so a drop can skip the dialog when the
   * Rx has been scheduled before.
   */
  const buildManualHandle = useCallback(
    (rxId: string, therapyRxId: string): TemplateHandle | null => {
      const rx = selectableRxs.find((r) => String(r.id) === String(rxId));
      if (!rx) return null;
      const trx = (rx.therapyRxs ?? []).find(
        (t: any) => String(t.id) === String(therapyRxId)
      ) as any;
      if (!trx) return null;
      const therapy: TemplateHandleTherapy = {
        therapyRxId: String(trx.id),
        ...templateTherapyFromPrescribed(trx, rxTarifGroupIdOf(rx), termDefaultDuration),
        order: trx.order ?? 0,
        remaining: Math.max((trx.amount ?? 0) - (trx.scheduledCount ?? 0), 0),
      };
      const inference = inferForRx(
        rx,
        [therapy.therapyRxId],
        existingAppointments,
        practiceAddress
      );
      return {
        id: `${rx.id}:manual:${therapy.therapyRxId}`,
        rxId: String(rx.id),
        rxNumber: String(rx.rxNumber ?? ""),
        rxDate: rx.date ? String(rx.date) : undefined,
        kind: "single",
        therapies: [therapy],
        label: therapy.abbreviation,
        remaining: therapy.remaining,
        provenance: "manual",
        inferredAddressId: inference.addressId,
        inferredVisitFees: inference.visitFees,
        inferredVisitType: inference.visitType,
        inferredAddressLabel: inference.addressLabel,
        inferredVisitFeeLabels: inference.visitFeeLabels,
        needsInput: inference.needsInput,
      };
    },
    [selectableRxs, existingAppointments, practiceAddress, termDefaultDuration]
  );

  /**
   * SUGGESTION bundles for a manually picked Rx: the same precedent-derived
   * groupings the auto handles get, so selection mode is never poorer than the
   * automatic one. Ids share the auto scheme (`:combined:`) on purpose — the
   * chip row dedups by id, so a manual suggestion silently yields to an
   * identical auto chip.
   */
  const buildManualBundles = useCallback(
    (rxId: string): TemplateHandle[] => {
      const rx = selectableRxs.find((r) => String(r.id) === String(rxId));
      if (!rx) return [];
      const trxs = (rx.therapyRxs ?? []) as any[];
      const byId = new Map(trxs.map((t) => [String(t.id), t]));
      return derivePrecedentGroups(precedentTherapiesOf(trxs), existingAppointments).map((group) => {
        const therapies: TemplateHandleTherapy[] = group
          .map((id) => byId.get(id))
          .filter(Boolean)
          .map((trx: any) => ({
            therapyRxId: String(trx.id),
            ...templateTherapyFromPrescribed(trx, rxTarifGroupIdOf(rx), termDefaultDuration),
            order: trx.order ?? 0,
            remaining: Math.max((trx.amount ?? 0) - (trx.scheduledCount ?? 0), 0),
          }))
          .sort((a, b) => a.order - b.order);
        const therapyRxIds = therapies.map((t) => t.therapyRxId);
        const inference = inferForRx(rx, therapyRxIds, existingAppointments, practiceAddress);
        return {
          id: `${rx.id}:combined:${therapyRxIds.join("-")}`,
          rxId: String(rx.id),
          rxNumber: String(rx.rxNumber ?? ""),
          rxDate: rx.date ? String(rx.date) : undefined,
          kind: "combined" as const,
          therapies,
          label: therapyNamingOrder(therapies, (t) => t.isSupplementary, (t) => t.abbreviation)
            .map((t) => t.abbreviation)
            .join(" + "),
          remaining: Math.min(...therapies.map((t) => t.remaining)),
          provenance: "precedent" as const,
          inferredAddressId: inference.addressId,
          inferredVisitFees: inference.visitFees,
          inferredVisitType: inference.visitType,
          inferredAddressLabel: inference.addressLabel,
          inferredVisitFeeLabels: inference.visitFeeLabels,
          needsInput: inference.needsInput,
        };
      });
    },
    [selectableRxs, existingAppointments, practiceAddress, termDefaultDuration]
  );

  // The widget shows automatically when it has handles to offer, or when the
  // user has explicitly summoned it. Both require the feature-preview gate.
  const autoVisible = canSeeFeaturePreview && !!selectedPatientId && handles.length > 0;
  const isVisible = autoVisible || (canSeeFeaturePreview && !!selectedPatientId && isOpen);

  return {
    handles,
    isVisible,
    autoVisible,
    isOpen,
    canSeeFeaturePreview,
    isLoading,
    isWidgetDataLoading,
    patientId: selectedPatientId,
    selectableRxs,
    defaultSelection,
    buildManualHandle,
    buildManualBundles,
    toggleOpen: CalendarStore.toggleTemplateWidget,
  };
};
